import React from 'react';
import { Crown, Star, Lock, CheckCircle } from 'lucide-react';

const UserLevelInfo = ({ userLevel, features, hasFeature, hasMinLevel, getFeatureInfo }) => {
    if (!userLevel) {
        return null;
    }

    // Определяем функции и их требования
    const functionRequirements = [
        {
            id: 1,
            name: 'bookmarks',
            displayName: 'Использование закладок',
            category: 'basic',
            requiredLevel: 1,
            description: 'Добавление открыток в избранное'
        },
        {
            id: 2,
            name: 'congratulations_send',
            displayName: 'Отправка поздравлений',
            category: 'social',
            requiredLevel: 5,
            description: 'Возможность отправлять поздравления'
        },
        {
            id: 3,
            name: 'congratulations_create',
            displayName: 'Создание поздравлений',
            category: 'creative',
            requiredLevel: 4,
            description: 'Создание поздравлений из открыток'
        }
    ];

    const getCategoryIcon = (category) => {
        switch (category) {
            case 'basic':
                return <CheckCircle className="w-4 h-4 text-green-500" />;
            case 'social':
                return <Star className="w-4 h-4 text-blue-500" />;
            case 'creative':
                return <Crown className="w-4 h-4 text-purple-500" />;
            default:
                return <CheckCircle className="w-4 h-4 text-gray-500" />;
        }
    };

    const getCategoryColor = (category) => {
        switch (category) {
            case 'basic':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'social':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'creative':
                return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                    {userLevel.level}
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                        {userLevel.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Уровень: {userLevel.level}
                    </p>
                </div>
            </div>

            {userLevel.description && (
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {userLevel.description}
                </p>
            )}

            <div className="space-y-4">
                <h4 className="text-md font-semibold text-gray-800 dark:text-gray-200">
                    Доступные функции:
                </h4>
                
                {functionRequirements.map((func) => {
                    const isAvailable = hasMinLevel(func.requiredLevel);
                    const featureInfo = getFeatureInfo(func.name);
                    
                    return (
                        <div
                            key={func.id}
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                                isAvailable
                                    ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                                    : 'bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600'
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                {isAvailable ? (
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                ) : (
                                    <Lock className="w-5 h-5 text-gray-400" />
                                )}
                                
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(func.category)}`}>
                                            {getCategoryIcon(func.category)}
                                            {func.category}
                                        </span>
                                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                            {func.displayName}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                        {func.description}
                                    </p>
                                </div>
                            </div>
                            
                            <div className="text-right">
                                <div className={`text-sm font-medium ${
                                    isAvailable 
                                        ? 'text-green-600 dark:text-green-400' 
                                        : 'text-gray-500 dark:text-gray-400'
                                }`}>
                                    {isAvailable ? 'Доступно' : `Уровень ${func.requiredLevel}`}
                                </div>
                                {!isAvailable && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        Нужно {func.requiredLevel - userLevel.level} уровней
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default UserLevelInfo;
